import React, { FunctionComponent, useEffect, useState } from "react";
import { Alert, AlertTitle } from "@material-ui/lab";
import { Link, Paper, Snackbar, Tooltip } from "@material-ui/core";
import * as InventoryApi from "../../../api/storeInventoryApi";
import { isOk } from "../../../result";
import { ProductSpoilageReport } from "../../../models/StoreInventory";
import MaterialTable, { MTableToolbar } from "@material-table/core";
import moment from "moment";
import { ConfirmDialog } from "../../../components/dialogs/ConfirmDialog";
import { useI18n } from "../../../hooks/useI18n";

interface ProductSpoilageProps {
  storeId: string;
  inventoryItem: any;
  onSave: (reports: any, quantity: number) => void;
  onSpoilageClick: (storeStocktakingId: string) => void;
}

export const ProductSpoilage: FunctionComponent<ProductSpoilageProps> = ({
  storeId,
  inventoryItem,
  onSave,
  onSpoilageClick
}: any) => {
  const [state, setState] = useState({
    reportedSpoilage: [] as ProductSpoilageReport[],
    reportsToRemove: [] as any,
    removeQuantity: 0,
    showConfirmRemoveReports: false,
    successBody: null as any,
    errors: {} as any,
    problem: null as any
  });

  const { t } = useI18n();

  useEffect(() => {
    if (inventoryItem && inventoryItem.reportedProductSpoilage)
      setState({
        ...state,
        reportedSpoilage: inventoryItem.reportedProductSpoilage
      });
  }, [inventoryItem]);

  const removeReportedProductSpoilage = async (ids: string[]) => {
    const result = await InventoryApi.removeReportedProductSpoilages(ids);

    if (isOk(result)) {
      const reports = state.reportedSpoilage.filter(
        report =>
          !ids.some(
            // @ts-ignore
            reportId => reportId === report.id
          )
      );

      onSave(reports, state.removeQuantity);

      return setState({
        ...state,
        reportedSpoilage: reports as ProductSpoilageReport[],
        reportsToRemove: [] as any,
        removeQuantity: 0,
        showConfirmRemoveReports: false,
        successBody: "The product spoilage has been removed"
      });
    }

    return setState({
      ...state,
      showConfirmRemoveReports: false,
      successBody: null,
      problem: result.details
    });
  };

  return storeId && inventoryItem ? (
    <>
      <MaterialTable
        title=""
        columns={[
          {
            title: "Date",
            field: "date",
            cellStyle: { padding: "8px" },
            render: spoilage => {
              return moment(spoilage.date).format("YYYY-MM-DD HH:mm");
            }
          },
          {
            title: "Quantity",
            field: "quantity",
            cellStyle: { padding: "8px" }
          },
          {
            title: t("common.tableHeaders.spoilageReason"),
            field: "reasonName",
            cellStyle: { padding: "8px" }
          },
          {
            title: "ReportedBy",
            field: "reportedBy",
            cellStyle: { padding: "8px" },
            render: rowData =>
              rowData.storeStocktakingId !== undefined ? (
                <Tooltip title={"Marks matching row in stocktaking"}>
                  <Link
                    style={{ cursor: "pointer" }}
                    onClick={() => onSpoilageClick(rowData.storeStocktakingId)}
                  >
                    Stocktaking
                  </Link>
                </Tooltip>
              ) : (
                rowData.reportedBy
              )
          }
        ]}
        data={
          state.reportedSpoilage
            ? (state.reportedSpoilage as ProductSpoilageReport[])
            : []
        }
        actions={[
          (rowData: any) => ({
            icon: "delete",
            tooltip:
              rowData.storeStocktakingId !== undefined
                ? "Generated by stocktaking"
                : "Remove",
            disabled: rowData.storeStocktakingId !== undefined,
            iconProps:
              rowData.storeStocktakingId !== undefined
                ? { style: { color: "rgba(0, 0, 0, 0.24)" } }
                : { style: { color: "rgba(0, 0, 0, 0.54)" } },
            onClick: (event, rowData: any) => {
              setState({
                ...state,
                showConfirmRemoveReports: true,
                reportsToRemove: [rowData.id],
                removeQuantity:
                  inventoryItem.reportedProductDelivery.length &&
                  moment(inventoryItem.reportedProductDelivery[0].created) <=
                    moment(rowData.date)
                    ? rowData.quantity
                    : 0
              });
            }
          })
        ]}
        components={{
          Container: props => <Paper {...props} elevation={0} />,
          Toolbar: props => (
            <div style={{ height: "0px", padding: 0 }}>
              <MTableToolbar {...props} />
            </div>
          )
        }}
        options={{
          headerStyle: {
            padding: "8px"
          },
          actionsCellStyle: { whiteSpace: "nowrap" },
          actionsColumnIndex: -1,
          pageSize: 5,
          search: false,
          pageSizeOptions: [20, 30, 40],
          sorting: false
        }}
        localization={{
          header: {
            actions: ""
          }
        }}
      />

      <ConfirmDialog
        visible={state.showConfirmRemoveReports}
        titleText={`Remove`}
        bodyText={
          "Are you sure you want to remove the selected reported product spoilages?"
        }
        confirmText={"Remove"}
        onConfirm={() => {
          if (state.reportsToRemove.length > 0) {
            removeReportedProductSpoilage(state.reportsToRemove);
          }
          return true;
        }}
        onCancel={() => {
          setState({ ...state, showConfirmRemoveReports: false });
        }}
      />

      <Snackbar
        open={state.successBody != null}
        autoHideDuration={2000}
        onClose={() => setState({ ...state, successBody: null })}
      >
        <Alert
          onClose={() => setState({ ...state, successBody: null })}
          severity="success"
        >
          {state.successBody ?? ""}
        </Alert>
      </Snackbar>

      <Snackbar
        open={state.problem != null}
        autoHideDuration={6000}
        onClose={() => setState({ ...state, problem: null })}
      >
        <Alert severity="error">
          <AlertTitle>
            {state.problem?.status}, {state.problem?.title}
          </AlertTitle>
          {state.problem?.detail} <strong>{state.problem?.traceId}</strong>
        </Alert>
      </Snackbar>
    </>
  ) : (
    <></>
  );
};
